"use client";
import { Badge, type BadgeProps } from "../Badge";
import { Skeleton } from "../Skeleton";

export interface FirmwareVersionBadgeProps extends Omit<BadgeProps, "variant" | "children"> {
  /** Firmware version string */
  version: string;
  /** Whether this is the latest version */
  isLatest?: boolean;
  /** Whether an update is available */
  updateAvailable?: boolean;
  /** Loading state */
  isLoading?: boolean;
}

/**
 * FirmwareVersionBadge
 *
 * Displays firmware version with update status indicators.
 * Green indicator if latest, orange if update available.
 * Compact display suitable for tables.
 *
 * @example
 * ```tsx
 * <FirmwareVersionBadge version="1.2.3" isLatest />
 * <FirmwareVersionBadge version="1.1.0" updateAvailable />
 * <FirmwareVersionBadge version="2.0.0-beta" />
 * ```
 */
export function FirmwareVersionBadge({
  version,
  isLatest,
  updateAvailable,
  isLoading,
  size = "sm",
  shape = "rounded",
  ...badgeProps
}: FirmwareVersionBadgeProps) {
  if (isLoading) {
    return <Skeleton className="inline-flex h-6 w-16" />;
  }

  let variant: BadgeProps["variant"] = "neutral";
  let label = version;

  if (isLatest) {
    variant = "success";
  } else if (updateAvailable) {
    variant = "warning";
    label = `${version} (Update)`;
  }

  return (
    <Badge variant={variant} size={size} shape={shape} {...badgeProps}>
      {label}
    </Badge>
  );
}
